import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import { episodesByDrama } from '@/mocks/episodes';
import { useFavorites } from '@/hooks/useFavorites';
import DramaCard from '@/components/feature/DramaCard';
import ShareButton from '@/components/feature/ShareButton';
import Seo from '@/components/feature/Seo';

export default function DramaDetail() {
  const { id } = useParams<{ id: string }>();
  const { isFavorite, toggle } = useFavorites();

  const drama = dramas.find((item) => item.id === id) ?? null;
  const episodeList = id ? episodesByDrama[id] ?? [] : [];

  const related = useMemo(() => {
    if (!drama) return [];
    const sameGenre = dramas.filter(
      (item) => item.id !== drama.id && item.genres.some((genre) => drama.genres.includes(genre)),
    );
    const others = dramas.filter((item) => item.id !== drama.id && !sameGenre.includes(item));
    return [...sameGenre, ...others].slice(0, 4);
  }, [drama]);

  if (!drama) {
    return (
      <div className="px-4 md:px-6 lg:px-10 py-24 flex flex-col items-center text-center">
        <Seo title="剧集不存在 - 上司的老婆" description="未找到对应的电视剧内容。" />
        <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
          <i className="ri-film-line"></i>
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-900">没有找到这部剧</h1>
        <p className="text-sm text-foreground-500 mt-2">它可能已经下架，或链接有误。</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回电视剧分类
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(drama.id);

  return (
    <div className="w-full">
      <Seo
        title={`${drama.title} - 上司的老婆剧情介绍与角色解析`}
        description={`${drama.title}剧情简介、主演阵容与角色解析。${drama.synopsis.slice(0, 60)}`}
        keywords={`${drama.title},${drama.title}剧情介绍,${drama.genres.join(',')},角色解析,${drama.actors.join(',')}`}
      />

      <div className="relative w-full h-[280px] md:h-[340px] overflow-hidden bg-primary-950">
        <img
          src={drama.banner}
          alt={`${drama.title} 剧照`}
          title={`${drama.title} - 剧情介绍与角色解析`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/30"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 md:px-6 lg:px-10 pb-6">
            <nav className="flex items-center gap-1.5 text-xs text-background-300 mb-3">
              <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category" className="hover:text-accent-400 transition-colors cursor-pointer">电视剧分类</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-100 line-clamp-1">{drama.title}</span>
            </nav>
            <div className="flex flex-wrap items-center gap-2">
              {drama.genres.map((genre) => (
                <span key={genre} className="px-2.5 py-1 rounded-full bg-background-50/15 text-background-100 text-xs backdrop-blur-sm">
                  {genre}
                </span>
              ))}
              <span className="px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs">{drama.status}</span>
            </div>
          </div>
        </div>
      </div>

      <section className="px-4 md:px-6 lg:px-10 -mt-10 md:-mt-14 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-8">
          <div className="hidden lg:block">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border-4 border-background-50">
              <img
                src={drama.cover}
                alt={`${drama.title} 封面`}
                title={`${drama.title} - 剧集封面`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex flex-col">
            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">{drama.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-foreground-600">
              <span className="flex items-center gap-1 text-accent-600 font-bold text-base">
                <i className="ri-star-fill"></i>
                {drama.rating.toFixed(1)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-accent-500"></i>
                热度 {drama.heat}
              </span>
              <span>{drama.year} 年</span>
              <span>{drama.region}</span>
              <span>共 {drama.episodeCount} 集</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-5 text-sm">
              <div className="flex gap-2">
                <span className="text-foreground-400 shrink-0">导演：</span>
                <span className="text-foreground-800">{drama.director}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-foreground-400 shrink-0">首播时间：</span>
                <span className="text-foreground-800">{drama.updateTime}</span>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <span className="text-foreground-400 shrink-0">主演：</span>
                <span className="text-foreground-800">{drama.actors.join(' / ')}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-5">
              {drama.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button
                type="button"
                onClick={() => toggle(drama.id)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? 'bg-accent-100 text-accent-900 border border-accent-300'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <i className={favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                {favorited ? '已收藏' : '收藏本剧'}
              </button>
              <ShareButton title={`${drama.title} - 上司的老婆影视剧内容站`} />
              <span className="text-xs text-foreground-400">收藏记录保存在你的浏览器本地</span>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 mt-10 md:mt-14">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">剧情简介</h2>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{drama.synopsis}</p>
          <p className="text-sm md:text-base text-foreground-600 leading-loose mt-4">
            该剧以{ drama.genres.join('、') }为核心类型，围绕主要人物的命运展开叙事。{drama.status}，目前共 {drama.episodeCount} 集，
            由 {drama.director} 执导，{drama.actors.join('、')} 等演员联袂出演。以下为部分角色的资料与分集剧情梗概。
          </p>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 mt-10 md:mt-14">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-1.5">角色介绍</h2>
        <p className="text-sm text-foreground-500 mb-5">将鼠标移到卡片上，可查看角色的详细介绍</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {drama.cast.map((member) => (
            <div key={member.id} className="group relative rounded-lg overflow-hidden border border-background-200 bg-background-50">
              <div className="w-full aspect-square overflow-hidden bg-background-200">
                <img
                  src={member.avatar}
                  alt={`${drama.title} 角色 ${member.name}`}
                  title={`${member.name} - ${drama.title} 角色介绍`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-bold text-foreground-950">{member.name}</h3>
                <p className="text-xs text-primary-600 mt-1">饰 {member.actor}</p>
                <p className="text-xs text-foreground-500 mt-2 leading-relaxed line-clamp-2">{member.role}</p>
              </div>
              <div className="absolute inset-0 bg-primary-950/92 text-background-100 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-center">
                <span className="text-xs text-accent-400 mb-2">角色档案</span>
                <h3 className="font-heading text-base font-bold text-background-50 mb-2">{member.name}</h3>
                <p className="text-xs leading-relaxed">{member.intro}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 mt-10 md:mt-14">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-1.5">剧集目录</h2>
        <p className="text-sm text-foreground-500 mb-5">点击具体集数，查看该集的剧情章节内容</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {Array.from({ length: drama.episodeCount }, (_, index) => index + 1).map((number) => {
            const episode = episodeList.find((item) => item.number === number);
            return (
              <Link
                key={number}
                to={`/detail/${drama.id}/episode/${number}`}
                className={`flex flex-col gap-1 p-3 rounded-lg border transition-colors cursor-pointer ${
                  episode
                    ? 'border-background-200 bg-background-50 hover:border-primary-400'
                    : 'border-dashed border-background-300 bg-background-100/60 hover:border-background-400'
                }`}
              >
                <span className={`text-xs ${episode ? 'text-accent-600 font-medium' : 'text-foreground-400'}`}>
                  第 {number} 集
                </span>
                <span className={`text-sm line-clamp-1 ${episode ? 'text-foreground-800' : 'text-foreground-400'}`}>
                  {episode ? episode.title : '内容待更新'}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 mt-12 md:mt-16">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">相关推荐</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {related.map((item) => (
            <DramaCard key={item.id} drama={item} />
          ))}
        </div>
      </section>
    </div>
  );
}